import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import {
  FiBell, FiPlus, FiTrash2, FiSend,
  FiUser, FiUsers, FiInfo, FiAlertCircle, FiTruck
} from 'react-icons/fi';

function mapRowToUi(row) {
  const t = String(row.type || '');
  let displayType = 'info';
  if (t.startsWith('admin_')) {
    displayType = t.slice(6);
  } else if (['info', 'success', 'warning', 'error'].includes(t)) {
    displayType = t;
  }
  const meta =
    row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const audience = meta.audience || null;
  return {
    ...row,
    displayType: ['info', 'success', 'warning', 'error'].includes(displayType)
      ? displayType
      : 'info',
    audience,
    typeLabel: t,
  };
}

function targetDescription(n) {
  if (n.audience === 'all') {
    return { icon: 'users', text: 'Diffusion : tous les utilisateurs' };
  }
  if (n.audience === 'clients') {
    return { icon: 'users', text: 'Diffusion : clients' };
  }
  if (n.audience === 'movers') {
    return { icon: 'truck', text: 'Diffusion : déménageurs' };
  }
  if (n.recipientEmail) {
    return {
      icon: 'user',
      text: `${n.recipientEmail}${n.recipientRole ? ` (${n.recipientRole})` : ''}`,
    };
  }
  return { icon: 'user', text: 'Destinataire (app)' };
}

function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    target: 'all',
    targetUserId: null,
  });

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getAdminNotificationsFeed({ limit: 100, offset: 0 });
      if (!res?.success) {
        throw new Error(res?.message || 'Réponse invalide');
      }
      const rows = Array.isArray(res.data) ? res.data : [];
      setNotifications(rows.map(mapRowToUi));
      setTotal(typeof res.total === 'number' ? res.total : rows.length);
    } catch (e) {
      console.error('Notifications admin:', e);
      setError(e.message || 'Impossible de charger les notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    try {
      setSending(true);
      const res = await api.broadcastAdminNotification({
        title: formData.title.trim(),
        message: formData.message.trim(),
        type: formData.type,
        target: formData.target,
      });
      if (!res?.success) {
        throw new Error(res?.message || 'Échec de l’envoi');
      }
      alert(res.message || 'Notification envoyée.');
      setShowCreateModal(false);
      setFormData({
        title: '',
        message: '',
        type: 'info',
        target: 'all',
        targetUserId: null,
      });
      await loadNotifications();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Erreur lors de l’envoi');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm('Supprimer cette notification de la base ?')) {
      return;
    }
    try {
      await api.deleteAdminNotification(id);
      await loadNotifications();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Erreur lors de la suppression');
    }
  };

  const getTypeIcon = (displayType) => {
    switch (displayType) {
      case 'info':
        return <FiInfo className="w-5 h-5 text-blue-400" />;
      case 'success':
        return <FiBell className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <FiAlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <FiAlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <FiBell className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTypeColor = (displayType) => {
    switch (displayType) {
      case 'info':
        return 'bg-blue-500/20 border-blue-500/30 text-blue-400';
      case 'success':
        return 'bg-green-500/20 border-green-500/30 text-green-400';
      case 'warning':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
      case 'error':
        return 'bg-red-500/20 border-red-500/30 text-red-400';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-400';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">Notifications système</h1>
          <p className="text-gray-400">
            Données réelles : historique des notifications en base (app + web). Les envois admin
            apparaissent ici après diffusion.
          </p>
          {!loading && total > 0 && (
            <p className="text-sm text-gray-500 mt-1">{total} notification(s) au total (100 affichées max.)</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center"
        >
          <FiPlus className="w-5 h-5 mr-2" />
          Nouvelle notification
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-900/30 border border-red-600/40 text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin" />
            <p className="text-gray-400 mt-4">Chargement des notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <FiBell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Aucune notification en base pour le moment</p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all"
            >
              Envoyer une annonce
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {notifications.map((notification) => {
              const target = targetDescription(notification);
              return (
                <div
                  key={notification.id}
                  className="p-6 hover:bg-gray-700/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div
                        className={`p-3 rounded-lg border ${getTypeColor(notification.displayType)}`}
                      >
                        {getTypeIcon(notification.displayType)}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-white">{notification.title}</h3>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getTypeColor(notification.displayType)}`}
                          >
                            {notification.displayType}
                          </span>
                          <span
                            className="px-2 py-1 text-xs rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 max-w-[200px] truncate"
                            title={notification.typeLabel}
                          >
                            {notification.type}
                          </span>
                          {notification.status && (
                            <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                              {notification.status}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-300 mb-3 whitespace-pre-wrap">{notification.message}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
                          <span className="flex items-center min-w-0">
                            {target.icon === 'users' && <FiUsers className="w-4 h-4 mr-1 flex-shrink-0" />}
                            {target.icon === 'truck' && <FiTruck className="w-4 h-4 mr-1 flex-shrink-0" />}
                            {target.icon === 'user' && <FiUser className="w-4 h-4 mr-1 flex-shrink-0" />}
                            <span className="truncate">{target.text}</span>
                          </span>
                          <span>•</span>
                          <span>{formatDate(notification.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        type="button"
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-2">Nouvelle notification</h2>
            <p className="text-sm text-gray-400 mb-6">
              Crée une entrée pour chaque utilisateur ciblé et envoie l’événement temps réel (Socket) s’il est en
              ligne.
            </p>
            <form onSubmit={handleCreateNotification}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Titre</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    required
                    maxLength={255}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Type (affichage)</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    >
                      <option value="info">Information</option>
                      <option value="success">Succès</option>
                      <option value="warning">Avertissement</option>
                      <option value="error">Erreur</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Cible</label>
                    <select
                      value={formData.target}
                      onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    >
                      <option value="all">Tous (clients + déménageurs)</option>
                      <option value="clients">Clients uniquement</option>
                      <option value="movers">Déménageurs uniquement</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={sending}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center disabled:opacity-50"
                >
                  <FiSend className="w-5 h-5 mr-2" />
                  {sending ? 'Envoi…' : 'Envoyer la notification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNotifications;
